/**
 * Oben auf „Verbindungen“: Konto und Guthaben. (27.10.2026, Martin)
 *
 * Wie bei anderen Diensten („Nachladen“ bei Claude Code): auf einen Blick, wie viel Guthaben noch da ist und wie
 * viel heute verbraucht ist, und EIN deutlicher Knopf zum Nachladen. Nachladen heißt hier: Vespera zahlt nichts und
 * bucht nichts ab. Man lädt beim Anbieter auf (Link zur Konsole) und trägt danach den Stand ein — das ist nur die
 * Grundlage für die Anzeige „Guthaben ca. …“.
 *
 * Gleiche Routen und gleiche Rechnung wie die Karte „Modell-Zugang“ (modell-zugang-karte.ts): GET /api/api-zugang,
 * POST /api/api-zugang/guthaben(-loeschen). Es werden keine Schlüssel angezeigt oder angefasst. Je Konto nur die
 * eigenen Daten — das entscheidet der Server anhand der Anmeldung.
 *
 * Nach jedem Speichern geht ZUGANG_GEAENDERT hinaus (die Seitenleisten-Kachel zieht nach); kommt es von anderswo
 * (Tageslimit, Modus, Schlüssel), liest diese Karte den Stand neu.
 */
import { zeichen } from "./zeichen.js";
import { guthabenKnoten, heuteSatz, hole, sende, ZUGANG_GEAENDERT, type ZugangStand } from "./modell-zugang-karte.js";

export const GUTHABEN_SEITE = "modelle";
export const GUTHABEN_FOKUS = "Modell-Zugang";
const LINK_KONSOLE = "https://console.anthropic.com/settings/billing";
const SATZ_NETZ = "Der Server ist gerade nicht erreichbar. Bitte versuch es gleich noch einmal.";
const SATZ_ZAHL = "Bitte nimm eine Zahl zwischen 0,01 und 100.000 Euro.";

let zaehler = 0;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

function zuEinstellungen(): void {
  document.dispatchEvent(new CustomEvent("vespera:go-settings", { detail: { sub: GUTHABEN_SEITE, fokus: GUTHABEN_FOKUS } }));
}

function textKnopf(text: string, aria?: string): HTMLButtonElement {
  const b = el("button", "konto-guthaben-knopf", text);
  b.type = "button";
  if (aria) b.setAttribute("aria-label", aria);
  return b;
}

export function createKontoGuthabenKarte(): HTMLElement {
  const id = `konto-guthaben-${++zaehler}`;
  const wrap = el("section", "konto-guthaben");
  wrap.setAttribute("aria-labelledby", `${id}-titel`);
  const titel = el("h3", "konto-guthaben-titel", "Konto und Guthaben");
  titel.id = `${id}-titel`;
  wrap.appendChild(titel);

  const status = el("p", "konto-guthaben-status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.hidden = true;

  // Antwort über das Abo (oder noch ohne Schlüssel): hier gibt es nichts nachzuladen.
  const aboBlock = el("div", "konto-guthaben-abo");
  aboBlock.hidden = true;
  const aboSatz = el("p", "konto-guthaben-satz", "Vespera antwortet über dein Abo. Dafür fällt hier kein Guthaben an.");
  const aboKnopf = textKnopf("Eigenen Schlüssel einrichten");
  aboKnopf.addEventListener("click", zuEinstellungen);
  aboBlock.append(aboSatz, aboKnopf);

  const apiBlock = el("div", "konto-guthaben-api");
  apiBlock.hidden = true;
  const stand = el("div", "konto-guthaben-stand");
  const heute = el("p", "konto-guthaben-satz");
  const reiheKnoepfe = el("div", "konto-guthaben-reihe");
  const nachladen = textKnopf("Guthaben nachladen");
  nachladen.classList.add("konto-guthaben-knopf-haupt");
  nachladen.setAttribute("aria-expanded", "false");
  nachladen.setAttribute("aria-controls", `${id}-nachladen`);
  const limitKnopf = textKnopf("Tageslimit ändern");
  limitKnopf.addEventListener("click", zuEinstellungen);
  reiheKnoepfe.append(nachladen, limitKnopf);

  const panel = el("div", "konto-guthaben-nachladen");
  panel.id = `${id}-nachladen`;
  panel.hidden = true;
  const schritte = el("ol", "konto-guthaben-schritte");
  const s1 = el("li", "");
  const link = el("a", "konto-guthaben-link", "Konsole des Anbieters öffnen");
  link.href = LINK_KONSOLE;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.setAttribute("aria-label", "Konsole des Anbieters öffnen (neuer Tab)");
  s1.append("Lade dein Guthaben beim Anbieter auf: ", link, ".");
  schritte.append(s1, el("li", "", "Schau dort nach, wie viel Guthaben du jetzt hast."), el("li", "", "Trag diesen Betrag hier ein und bestätige."));
  const form = el("form", "konto-guthaben-form");
  form.setAttribute("autocomplete", "off");
  const label = el("label", "konto-guthaben-label", "Mein Guthaben beträgt");
  const feld = el("input", "konto-guthaben-feld");
  feld.id = `${id}-feld`;
  label.htmlFor = feld.id;
  feld.type = "number";
  feld.min = "0.01";
  feld.max = "100000";
  feld.step = "0.01";
  feld.inputMode = "decimal";
  const euro = el("span", "konto-guthaben-euro", "€");
  const bestaetigen = el("button", "konto-guthaben-knopf konto-guthaben-knopf-haupt", "Stand bestätigen");
  bestaetigen.type = "submit";
  const feldReihe = el("div", "konto-guthaben-feldreihe");
  feldReihe.append(feld, euro, bestaetigen);
  const weg = textKnopf("Eintrag löschen", "Eintrag löschen: eingetragenes Guthaben entfernen");
  form.append(label, feldReihe);
  panel.append(
    schritte,
    form,
    el("p", "konto-guthaben-klein", "Vespera zahlt nichts und bucht nichts ab. Der Betrag dient nur der Anzeige: Guthaben minus das, was seitdem über Vespera verbraucht wurde."),
    weg,
  );
  apiBlock.append(stand, heute, reiheKnoepfe, panel);
  wrap.append(status, aboBlock, apiBlock);

  const melde = (text: string, fehler = false): void => {
    status.hidden = false;
    status.dataset.ton = fehler ? "is-fehler" : "is-ok";
    // Ein Fehler wird sofort vorgelesen (alert), eine Bestätigung höflich (status).
    status.setAttribute("role", fehler ? "alert" : "status");
    status.setAttribute("aria-live", fehler ? "assertive" : "polite");
    const marke = el("span", "konto-guthaben-marke");
    marke.innerHTML = zeichen(fehler ? "schliessen" : "haken", 16);
    status.replaceChildren(marke, el("span", "", text));
  };

  const zeige = (s: ZugangStand | null): void => {
    if (!s) {
      aboBlock.hidden = true;
      apiBlock.hidden = true;
      melde("Der Stand ist gerade nicht abrufbar.", true);
      return;
    }
    const mitApi = s.modus === "api" && s.schluessel.gespeichert;
    aboBlock.hidden = mitApi;
    apiBlock.hidden = !mitApi;
    if (!mitApi) return;
    const kn = guthabenKnoten(s.guthaben, "konto-guthaben-zeile");
    stand.replaceChildren(...(kn.length ? kn : [el("p", "konto-guthaben-zeile konto-guthaben-leer", "Noch kein Guthaben eingetragen.")]));
    heute.textContent = `Heute ${heuteSatz(s.heuteKostenCent, s.limitCent)}`;
    weg.hidden = !s.guthaben;
    nachladen.textContent = s.guthaben ? "Guthaben nachladen" : "Guthaben eintragen";
  };

  // Laufnummer: eine ältere, später eintreffende Antwort überschreibt nie einen neueren Stand.
  let lauf = 0;
  const frisch = async (): Promise<void> => {
    const nr = ++lauf;
    const s = await hole();
    if (nr === lauf) zeige(s);
  };

  nachladen.addEventListener("click", () => {
    const an = nachladen.getAttribute("aria-expanded") !== "true";
    nachladen.setAttribute("aria-expanded", String(an));
    panel.hidden = !an;
    if (an) feld.focus();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const betrag = Number(feld.value.replace(",", "."));
    if (feld.value.trim() === "" || !Number.isFinite(betrag) || betrag < 0.01 || betrag > 100000) {
      melde(SATZ_ZAHL, true);
      return;
    }
    bestaetigen.disabled = true;
    void sende("guthaben", { euro: betrag }, "guthaben-karte").then((r) => {
      bestaetigen.disabled = false;
      lauf++; // eine noch laufende Abfrage von vorher ist jetzt veraltet
      if (r.ok && r.daten) {
        feld.value = "";
        zeige(r.daten as ZugangStand);
        melde("Dein Stand ist eingetragen. Ab jetzt rechnet Vespera mit.");
      } else melde(r.daten?.satz ?? (r.status === 429 ? "Bitte warte eine Minute, dann geht es weiter." : SATZ_NETZ), true);
    });
  });

  weg.addEventListener("click", () => {
    if (weg.disabled) return;
    weg.disabled = true; // kein Doppelklick
    void sende("guthaben-loeschen", {}, "guthaben-karte").then((r) => {
      weg.disabled = false;
      lauf++;
      if (r.ok && r.daten) {
        zeige(r.daten as ZugangStand);
        melde("Der eingetragene Stand ist gelöscht.");
      } else melde(SATZ_NETZ, true);
    });
  });

  // Etwas anderswo gespeichert (Tageslimit, Modus, Schlüssel) oder zurück vom Anbieter: Stand neu lesen.
  const beiAenderung = (ev: Event): void => {
    if (!wrap.isConnected) return;
    if ((ev as CustomEvent<{ von?: string }>).detail?.von !== "guthaben-karte") void frisch();
  };
  const beiRueckkehr = (): void => {
    if (wrap.isConnected && document.visibilityState === "visible") void frisch();
  };
  document.addEventListener(ZUGANG_GEAENDERT, beiAenderung);
  document.addEventListener("visibilitychange", beiRueckkehr);
  const wache = window.setInterval(() => {
    if (wrap.isConnected) return;
    window.clearInterval(wache);
    document.removeEventListener(ZUGANG_GEAENDERT, beiAenderung);
    document.removeEventListener("visibilitychange", beiRueckkehr);
  }, 30_000);

  void frisch();
  return wrap;
}
